import AbstractFilterControl from "./AbstractFilterControl";

import type { Attribute } from "@esWebAPI/es/model/Feature";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { AndCriteria, Criteria } from "@esWebAPI/es/model/Criteria";

export default class DateFilterControl extends AbstractFilterControl {
    private min: number;
    private max: number;
    private minValue: number;
    private maxValue: number;

    constructor(layerViewModel: LayerViewModel, attribute: Attribute, ranges) {
        super(layerViewModel, "DateRange", attribute);

        if (this.isEmpty(ranges)) {
            return;
        }

        const minValueLOCAL = ranges.min + new Date(ranges.min).getTimezoneOffset() * 60000;
        const maxValueLOCAL = ranges.max + new Date(ranges.max).getTimezoneOffset() * 60000;

        this.minValue = this.min = minValueLOCAL;
        this.maxValue = this.max = maxValueLOCAL;
    }

    private isEmpty(range): boolean {
        if (range.min !== undefined && range.max !== undefined) {
            return false;
        } else {
            return true;
        }
    }

    private mergeCriteria(criteria: Criteria[]): AndCriteria {
        var min = -Infinity;
        var max = Infinity;
        var lessCount = 0, greaterCount = 0;
        criteria.forEach(function(crit) {
            if (crit.$type === "DateLessEqual") {
                max = Math.min(max, crit.value);
                lessCount++;
            } else {
                min = Math.max(min, crit.value);
                greaterCount++;
            }
        });

        if (lessCount === 0 || greaterCount === 0) {
            throw new Error("Can not merge invalid criteria");
        }

        if (min > max) {
            max = min; // sanity check
        }

        return {
            $type: "And",
            criteria: [{
                $type: "DateGreaterEqual",
                attr: criteria[0].attr,
                value: min
            }, {
                $type: "DateLessEqual",
                attr: criteria[0].attr,
                value: max
            }]
        };
    }

    toCriteria(): Criteria {
        var minValueUTC = this.minValue - new Date(this.minValue).getTimezoneOffset() * 60000;
        var maxValueUTC = this.maxValue - new Date(this.maxValue).getTimezoneOffset() * 60000;

        return {
            $type: "And",
            criteria: [{
                $type: "DateGreaterEqual",
                attr: this.getAttrName(),
                value: minValueUTC
            }, {
                $type: "DateLessEqual",
                attr: this.getAttrName(),
                value: maxValueUTC
            }]
        };
    }

    fromCriteria(criteria: Criteria | Criteria[]) {
        if (criteria.$type === "And") {
            criteria = criteria.criteria;
        }
        
        if (criteria.length > 2) {
            criteria = this.mergeCriteria(criteria);
        }

        const minValue = criteria.get("$type", "DateGreaterEqual").value;
        const maxValue = criteria.get("$type", "DateLessEqual").value;
        const minValueLOCAL = minValue + new Date(minValue).getTimezoneOffset() * 60000;
        const maxValueLOCAL = maxValue + new Date(maxValue).getTimezoneOffset() * 60000;

        this.minValue = minValueLOCAL;
        this.maxValue = maxValueLOCAL;
    }

    isValid(): boolean {
        return this.checkValues(this.minValue, this.maxValue) === null;
    }

    setRange(attributeRanges: Record<string, any>) {
        if (this.isLocked()) {
            return; // if filter is locked, we don't want to change it
        }
        
        var range = attributeRanges[this.getAttrName()];
        if (this.isEmpty(range)) {
            return;
        }

        this.min = range.min;
        this.max = range.max;

        let changed = false;
        if (this.minValue < this.min) {
            this.minValue = this.min;
            changed = true;
        }

        if (this.maxValue > this.max) {
            this.maxValue = this.max;
            changed = true;
        }

        if (changed) {
            this.eventChannel.fireEvent("onUpdate");
        }
    }

    update(minValue: number, maxValue: number) {
        const err = this.checkValues(minValue, maxValue);
        if (err) {
            throw new Error(err);
        }

        this.minValue = minValue;
        this.maxValue = maxValue;

        this.eventChannel.fireEvent("onUpdate");
    }

    checkValues(x: number, y: number): string | null {
        if (isNaN(x) || isNaN(y)) {
            return $i18n("_Filter_Error_Msg_Not_Supported");
        }

        if (x > y) {
            return $i18n("_Filter_Error_Date_Msg_Minimum_Exceed_Maximum");
        }

        return null;
    }

    merge(filter: AbstractFilterControl) {
        const criteria: Criteria[] = [this.toCriteria(true), filter.toCriteria(true)];
        this.fromCriteria(criteria);
    }

    static supportCriteria(criteria: Criteria | Criteria[]): boolean {
        if (criteria.$type === "And") {
            criteria = criteria.criteria;
        }
        
        return Array.isArray(criteria)
            && criteria.length > 1
            && criteria.every(function (crit) { return crit.$type === "DateLessEqual" || crit.$type === "DateGreaterEqual";})
            && criteria.some(function (crit) { return crit.$type === "DateLessEqual";})
            && criteria.some(function (crit) { return crit.$type === "DateGreaterEqual";})
            && criteria.every(function(crit) { return crit.attr === criteria[0].attr;});
    }

    static supportAttribute(attrType: string) {
        return attrType === "TIMESTAMP";
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute: Attribute, ranges) {
        return new DateFilterControl(layerViewModel, attribute, ranges[attribute.name]).init().done((filter: DateFilterControl) => {
            filter.fromCriteria(criteria);
        });
    }

    // es.control.DateFilter = DateFilter;
}
